import { CustomRoute } from "@/interfaces";
import { AppWindowMac, Building2, DollarSign, LayoutDashboard, Settings, User, Users, Users2, FileText, CheckCircle, BarChart3, ShoppingCart, Receipt, Target, AlertTriangle, Edit3, Gavel, ClipboardCheck, Link2 } from "lucide-react";
import React from "react";

const Dashboard = React.lazy(() => import("../modules/Admin/Dashboard"));
const Services = React.lazy(() => import("../modules/Admin/Services"));
const ClientManagement = React.lazy(() => import("../modules/Admin/ClientManagement/index"));
const VendorManagement = React.lazy(() => import("../modules/Admin/VendorManagement/index"));
const EmployeeManagement = React.lazy(() => import("../modules/Admin/EmployeeManagement/index"));
const PermissionTemplates = React.lazy(() => import("../modules/Admin/PermissionTemplates/index"));
const CommissionTemplates = React.lazy(() => import("../modules/Admin/CommissionManagement/TemplatesPage"));
const AllCommissions = React.lazy(() => import("../modules/Admin/CommissionManagement/AllCommissions"));
const CommissionPayouts = React.lazy(() => import("../modules/Admin/CommissionManagement/Payouts"));
const CommissionApprovals = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionApprovals"));
const CommissionConflicts = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionConflicts"));
const CustomCommissions = React.lazy(() => import("../modules/Admin/CommissionManagement/CustomCommissions"));
const CommissionDisputes = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionDisputes"));
const InvoiceCommissionMapping = React.lazy(() => import("../modules/Admin/CommissionManagement/InvoiceCommissionMapping"));
const TeamworkProjects = React.lazy(() => import("../modules/Admin/TeamWorkProjects"));
// const NativeProjects = React.lazy(() => import("../modules/Admin/NativeProjects"));
const StripePayment = React.lazy(() => import("../modules/Admin/Billinginvoices/StripePayment"));
const PaymentHistory = React.lazy(() => import("../modules/Admin/Billinginvoices/PaymentHistory"));
const EditProfilePage = React.lazy(() => import("../modules/Profile/EditProfile"));
const AllNotifications = React.lazy(() => import("../modules/Notifications/AllNotifications"));
// const AdminInvoices = React.lazy(() => import("../modules/Admin/Invoices/MyInvoices"));
const CreateAdminInvoice = React.lazy(() => import("../modules/Admin/Invoices/CreateInvoicePage"));
const AllInvoices = React.lazy(() => import("../modules/Admin/Invoices/AllInvoices"));
const InvoiceHistory = React.lazy(() => import("../modules/Admin/Invoices/InvoiceHistory"));
const NetSuiteVendorBills = React.lazy(() => import("../modules/Admin/ListVendorBills/AllBills"));
const QuickBooksVendorBills = React.lazy(() => import("../modules/Admin/ListVendorBills/QuickBooksAllBills"))
const VendorBillApprovals = React.lazy(() => import("../modules/Finance/VendorBillApprovals"));
// const CreateBillPage = React.lazy(() => import("../modules/Admin/Bills/CreateBillPage"));
const RecurringInvoices = React.lazy(() => import("../modules/Admin/Invoices/RecurringInvoices"));
const NextRecurringInvoices = React.lazy(() => import("../modules/Finance/NextRecurringInvoices"));
const Reports = React.lazy(() => import("../modules/SuperAdmin/Reports/index"));
const CommissionReport = React.lazy(() => import("../modules/SuperAdmin/Reports/CommissionReport"));
// const ClosedWonLeadsPage = React.lazy(() => import("../modules/Admin/OMS/ClosedWonLeads/index.tsx"));
const OrdersPage = React.lazy(() => import("../modules/Admin/OMS/Orders/index.tsx"));
const CreateOrderPage = React.lazy(() => import("../modules/Admin/OMS/Orders/CreateOrderPage.tsx"));
const OrderApprovalPage = React.lazy(() => import("../modules/Admin/OMS/OrderApprovals/index.tsx"));
const DealsPage = React.lazy(() => import("../modules/Admin/CRM/Deals/index.tsx"));
const DealDetailsPage = React.lazy(() => import("../modules/Admin/CRM/Deals/DealDetailsPage.tsx"));
const CrmCompaniesPage = React.lazy(() => import("../modules/Admin/CRM/Companies/index.tsx"));
const CrmCompanyDetailsPage = React.lazy(() => import("../modules/Admin/CRM/Companies/CompanyDetailsPage.tsx"));
const CrmContactsPage = React.lazy(() => import("../modules/Admin/CRM/Contacts/index.tsx"));
const CrmMeetingsPage = React.lazy(() => import("../modules/Admin/CRM/Meetings/index.tsx"));
// const ProjectsPage = React.lazy(() => import("../modules/Admin/OMS/Projects/index.tsx"));
const ReleaseAndPurchaseOrderReport = React.lazy(() => import("../modules/Admin/pages/ReleaseAndPurchaseOrderReport").then(m => ({ default: m.ReleaseAndPurchaseOrderReport })));
const DailyReportsPage = React.lazy(() => import("../modules/Admin/OMS/DailyReports/DailyReportsPage.tsx"));
const PowerBiPage = React.lazy(() => import("@/modules/SuperAdmin/PowerBi"));
const PowerBiViewer = React.lazy(() => import("@/modules/SuperAdmin/PowerBi/PowerBiViewer"));
import { useUserStore } from "@/stores/userStore";
import ApprovalManagement from "@/modules/Finance/ApprovalManagement";

/**
 * Get admin routes
 * @returns Array of admin routes
 */
const getAdminRoutes = (): CustomRoute[] => {
  // Read fresh on every call — this must NOT be hoisted to module scope, since that would
  // freeze it at whatever company was active the first time this module was ever imported
  // (e.g. before company/auth data loaded, or before a later company switch), rather than
  // reflecting the actual current company when routes are computed.
  const currentCompany = useUserStore.getState().currentCompany;
  // NetSuite is the default/fallback (matches the backend convention used throughout the
  // app) — only an explicit "quickbook" match (any casing) counts as QuickBooks, so this
  // doesn't silently fall through to QuickBooks for casing variants like "Quickbooks".
  const isQuickBooks = String(currentCompany?.invoicingAccounting || "").trim().toLowerCase().includes("quickbook");

  return [
    {
      path: "/",
      id: "Admin",
      element: <Dashboard />,
      menuName: "Dashboard",
      icon: LayoutDashboard,
      highlightOn: ["dashboard"],
    },
    {
      path: "/services",
      id: "Admin",
      element: <Services />,
      menuName: "Services",
      icon: Settings,
      highlightOn: ["services", "service"],
    },
    // Client Management - Note: Client CRUD (create/edit/delete) is SuperAdmin only.
    // This module is for managing client team members, services, Users, and commissions.
    {
      path: "/client-management",
      id: "Admin",
      element: <ClientManagement />,
      menuName: "Client Management",
      icon: User,
      highlightOn: ["companies", "company", "all-vendors"],
      submenu: [
        {
          path: "/client-management",
          id: "Admin",
          element: <ClientManagement />,
          menuName: "Client Management",
          icon: User,
          highlightOn: ["companies", "company"],
        },
        {
          path: "/projects",
          id: "Admin",
          menuName: "Projects",
          element: <TeamworkProjects />,
          icon: Building2,
          highlightOn: ["all-vendors"],
        },
        // {
        //   path: "/native-projects",
        //   id: "Admin",
        //   menuName: "Projects",
        //   element: <NativeProjects />,
        //   icon: Building2,
        //   highlightOn: ["native-projects"],
        // }
      ]
    },
    {
      path: "/vendor-management",
      id: "Admin",
      element: <VendorManagement />,
      menuName: "Vendor Management",
      icon: Building2,
      highlightOn: ["VendorManagement", "organization"],
      permissionKey: "vendorManagement",
    },
    {
      path: "/employee-management",
      id: "Admin",
      element: <EmployeeManagement />,
      menuName: "Employee Management",
      icon: Users2,
      highlightOn: ["employees", "employee", "permission-templates", "employee-commissions"],
      permissionKey: "employeeManagement",
      submenu: [
        {
          path: "/employee-management",
          id: "Admin",
          element: <EmployeeManagement />,
          menuName: "All Employees",
          icon: Users2,
          highlightOn: ["employees", "employee"],
        },
        {
          path: "/employee-management/permission-templates",
          id: "Admin",
          element: <PermissionTemplates />,
          menuName: "Permission Templates",
          icon: Users2,
          highlightOn: ["permission-templates"],
          permissionKey: "employeeManagement",
        },
      ],
    },
    // Commission approvers (Super Admin → Commission Approvers) see this as a top-level menu only
    // when the Commissions menu is hidden for them; otherwise it shows as the Commissions submenu
    // entry below (UserMenu.tsx). This entry is the one that registers the route.
    {
      path: "/my-commission-approvals",
      id: "Admin",
      element: <CommissionApprovals />,
      menuName: "Commission Approvals",
      icon: ClipboardCheck,
      highlightOn: ["my-commission-approvals"],
    },
    {
      path: "/commissions",
      id: "Admin",
      element: <AllCommissions />,
      menuName: "Commissions",
      icon: DollarSign,
      highlightOn: ["commissions", "commission-templates", "commission-payouts"],
      submenu: [
        {
          path: "/commissions/all",
          id: "Admin",
          element: <AllCommissions />,
          menuName: "All Commissions",
          icon: DollarSign,
          highlightOn: ["commissions-all"],
        },
        {
          path: "/commissions/payouts",
          id: "Admin",
          element: <CommissionPayouts />,
          menuName: "Payouts",
          icon: DollarSign,
          highlightOn: ["commission-payouts"],
        },
        {
          // Menu link only -- the route itself is registered by the top-level entry.
          path: "/my-commission-approvals",
          id: "Admin",
          element: null,
          menuName: "Commission Approvals",
          icon: ClipboardCheck,
          highlightOn: ["my-commission-approvals"],
        },
        {
          path: "/commissions/conflicts",
          id: "Admin",
          element: <CommissionConflicts />,
          menuName: "Conflicts",
          icon: AlertTriangle,
          highlightOn: ["commission-conflicts"],
        },
        {
          path: "/commissions/custom",
          id: "Admin",
          element: <CustomCommissions />,
          menuName: "Custom Commissions",
          icon: Edit3,
          highlightOn: ["commission-custom"],
        },
        {
          path: "/commissions/disputes",
          id: "Admin",
          element: <CommissionDisputes />,
          menuName: "Disputes",
          icon: Gavel,
          highlightOn: ["commission-disputes"],
        },
        {
          path: "/commissions/invoice-mapping",
          id: "Admin",
          element: <InvoiceCommissionMapping />,
          menuName: "Invoice Mapping",
          icon: Link2,
          highlightOn: ["commission-invoice-mapping"],
        },
        {
          path: "/commissions/templates",
          id: "Admin",
          element: <CommissionTemplates />,
          menuName: "Templates",
          icon: DollarSign,
          highlightOn: ["commission-templates"],
        },
      ],
    },
    // {
    //   path: "/teamwork-projects",
    //   id: "Admin",
    //   menuName: "Teamwork Projects",
    //   element: <TeamworkProjects />,
    //   icon: Building2,
    //   highlightOn: ["all-vendors"],
    // },
    {
      path: "",
      id: "Admin",
      element: null,
      menuName: "Financials",
      icon: AppWindowMac,
      highlightOn: ["billing", "invoices"],
      submenu: [
        {
          path: "/billing/all-invoices",
          id: "Admin",
          element: <AllInvoices />,
          menuName: "All Invoices",
          icon: Building2,
          highlightOn: ["all-invoices"],
        },
        {
          path: "/netsuite/approvals",
          id: "Finance",
          element: <ApprovalManagement provider="netsuite" />,
          menuName: "Invoice Approvals",
          icon: CheckCircle,
          highlightOn: ["netsuite-approvals"],
        },
        {
          path: "/quickbooks/approvals",
          id: "Finance",
          element: <ApprovalManagement provider="quickbooks" />,
          menuName: "Invoice Approvals",
          icon: CheckCircle,
          highlightOn: ["quickbooks-approvals"],
        },
        {
          path: "/billing/invoice-history",
          id: "Admin",
          element: <InvoiceHistory />,
          menuName: "Invoice History",
          icon: Building2,
          highlightOn: ["invoice-history"],
        },
        {
          path: "/invoices/create",
          id: "Admin",
          element: <CreateAdminInvoice />,
          menuName: "Create Invoice",
          icon: Building2,
          highlightOn: ["create-invoice"],
        },
        {
          path: "/invoices/edit/:id",
          id: "Admin",
          element: <CreateAdminInvoice />,
          menuName: "Edit Invoice",
          hideInMenu: true,
          highlightOn: ["all-invoices"],
        },
        {
          path: "/billing/recurring-invoices",
          id: "Admin",
          element: <RecurringInvoices />,
          menuName: "Recurring Invoices",
          icon: Building2,
          highlightOn: ["recurring-invoices"],
          hideInMenu: !isQuickBooks,
        },
        {
          path: "/billing/next-recurring-invoices",
          id: "Admin",
          element: <NextRecurringInvoices />,
          menuName: "Upcoming Invoices",
          icon: Building2,
          highlightOn: ["next-recurring-invoices"],
        },
        {
          path: "/billing/payment-history",
          id: "Admin",
          element: <PaymentHistory />,
          menuName: "Payment History",
          icon: Receipt,
          highlightOn: ["payment-history"],
        },
        {
          path: "/billing/vendor-bills",
          id: "Admin",
          element: isQuickBooks ? <QuickBooksVendorBills /> : <NetSuiteVendorBills />,
          menuName: "Vendor Bills",
          icon: FileText,
          highlightOn: ["vendor-bills"],
        },
        {
          path: "/billing/vendor-bill-approvals",
          id: "Admin",
          element: <VendorBillApprovals />,
          menuName: "Vendor Bills Approvals",
          icon: FileText,
          highlightOn: ["vendor-bill-approvals"],
        },
        {
          path: "/billing/stripe-payment",
          id: "Admin",
          element: <StripePayment />,
          menuName: "Stripe Payment",
          icon: Building2,
          highlightOn: ["add-vendor"],
        },
        {
          path: "/:provider/approvals/edit/:id",
          id: "Finance",
          element: <CreateAdminInvoice isApprovalEdit={true} />,
          menuName: "Edit Invoice Approval",
          hideInMenu: true,
          highlightOn: ["netsuite-approvals", "quickbooks-approvals"],
        },
      ],
    },
    {
      path: "",
      id: "Admin",
      element: null,
      menuName: "OMS",
      icon: ShoppingCart,
      highlightOn: ["billing", "invoices"],
      permissionKey: "oms",
      submenu: [
        // Stripe Payment, All Invoices, Create Invoice, and My Invoices
        // {
        //   path: "/oms/closed-won",
        //   id: "Admin",
        //   element: <ClosedWonLeadsPage />,
        //   menuName: "Closed Won Leads",
        //   highlightOn: ["closed-won"],
        // },
        {
          path: "/oms/orders",
          id: "Admin",
          element: <OrdersPage />,
          menuName: "Orders",
          highlightOn: ["orders"],
        },
        {
          path: "/oms/orders/create",
          id: "Admin",
          element: <CreateOrderPage />,
          hideInMenu: true,
          menuName: "Orders",
          highlightOn: ["orders"],
        },
        {
          path: "/oms/approvals",
          id: "Admin",
          element: <OrderApprovalPage />,
          menuName: "Order Approvals",
          highlightOn: ["approvals"],
        },
        // {
        //   path: "/oms/projects",
        //   id: "Admin",
        //   element: <ProjectsPage />,
        //   menuName: "Projects",
        //   highlightOn: ["projects"],
        // },
        {
          path: "/oms/ro-po-report",
          id: "Admin",
          element: <ReleaseAndPurchaseOrderReport />,
          menuName: "RO/PO Report",
          highlightOn: ["ro-po-report"],
        },
        {
          path: "/oms/consumption-report",
          id: "Admin",
          element: <DailyReportsPage />,
          menuName: "Consumption Report",
          highlightOn: ["daily-reports"],
          permissionKey: "oms_operations",
        },
        {
          // Use a more descriptive path if it's for editing
          path: "/oms/orders/:id/edit",
          id: "edit-order-route", // Unique ID
          element: <CreateOrderPage />, // Your component from the previous step
          menuName: "Edit Order",
          // This ensures the "Orders" menu item stays highlighted while editing
          highlightOn: ["orders", "edit-order"],
          hideInMenu: true, // Custom flag to prevent a broken link in the sidebar
        }
      ],
    },
    {
      path: "",
      id: "Admin",
      element: null,
      menuName: "CRM",
      icon: Target,
      highlightOn: ["deals"],
      permissionKey: "oms_sales",
      submenu: [
        {
          path: "/crm/contacts",
          id: "Admin",
          element: <CrmContactsPage />,
          menuName: "Contacts",
          highlightOn: ["contacts"],
        },
        {
          path: "/crm/companies",
          id: "Admin",
          element: <CrmCompaniesPage />,
          menuName: "Companies",
          highlightOn: ["companies"],
        },
        {
          path: "/crm/companies/:id",
          id: "Admin",
          element: <CrmCompanyDetailsPage />,
          hideInMenu: true,
          menuName: "Companies",
          highlightOn: ["companies"],
        },
        {
          path: "/crm/deals",
          id: "Admin",
          element: <DealsPage />,
          menuName: "Deals",
          highlightOn: ["deals"],
        },
        {
          path: "/crm/deals/:id",
          id: "Admin",
          element: <DealDetailsPage />,
          hideInMenu: true,
          menuName: "Deals",
          highlightOn: ["deals"],
        },
        {
          path: "/crm/meetings",
          id: "Admin",
          element: <CrmMeetingsPage />,
          menuName: "Meetings",
          highlightOn: ["meetings"],
        },
      ],
    },
    {
      path: "/reports",
      id: "Admin",
      element: <Reports />,
      menuName: "Reports",
      icon: BarChart3,
      highlightOn: ["reports"],
      submenu: [
        {
          path: "/reports",
          id: "Admin",
          element: <Reports />,
          menuName: "General Reports",
          icon: BarChart3,
          highlightOn: ["reports"],
        },
        {
          path: "/reports/commission",
          id: "Admin",
          element: <CommissionReport />,
          menuName: "Commission Report",
          icon: DollarSign,
          highlightOn: ["reports-commission"],
        }
      ]
    },
    {
      path: "/powerbi",
      id: "Admin",
      element: <PowerBiPage />,
      menuName: "Power BI",
      icon: FileText,
      highlightOn: ["powerbi"],
    },
    {
      path: "/powerbi/view/:id",
      id: "Admin",
      element: <PowerBiViewer />,
      menuName: "Power BI Viewer",
      hideInMenu: true,
      highlightOn: ["powerbi"],
    },
    {
      path: "/notifications",
      id: "Admin",
      element: <AllNotifications />,
      menuName: "Notifications",
      hideInMenu: true,
      highlightOn: ["notifications"],
    },
    {
      path: "/profile",
      id: "Admin",
      element: <EditProfilePage />,
      menuName: "Profile",
      icon: Users,
      highlightOn: ["profile"],
    },
  ];
};

// Export the function for dynamic route generation
export { getAdminRoutes };